import * as FC from "@fluentui/react-components";
import * as FDP from "@fluentui/react-datepicker-compat";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleGrid } from "../simple_grid";
import { useDrawer } from "../use_drawer";
import { toDateString, toDateTimeString } from "../../date_utils";
import { useToast } from "../../hooks/useToast";
import {
    EQUIPMENT_DEFAULT_PAGE_SIZE,
    cancelServiceRecord,
    completeServiceRecord,
    equipmentOperationalStatusLabel,
    formatDateOnly,
    getEquipment,
    getServiceRecord,
    listServiceRecords,
    serviceRecordActions,
    serviceRecordSortOptions,
    serviceStatusLabel,
    serviceStatusOptions,
    serviceTypeLabel,
    serviceTypeOptions,
    serviceUsageUnitOptions,
    occupyingSessionsFromError,
    startServiceRecord,
    toDateOnly,
    voidServiceRecord,
} from "../../services/equipment_service";
import { AttachmentPanel } from "./attachment_panel";
import { ServiceRecordForm } from "./service_record_form";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    filters: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "end",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("12px"),
        backgroundColor: FC.tokens.colorNeutralBackground2,
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
    },
    field: {
        display: "flex",
        minWidth: "150px",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        ...FC.shorthands.gap("8px"),
    },
    dialogBody: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    row: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        ...FC.shorthands.gap("10px"),
    },
    mono: {
        fontFamily: "Consolas, monospace",
    },
    muted: {
        color: FC.tokens.colorNeutralForeground3,
    },
});

const pageSizeOptions = [20, 50, 100];

const statusBadgeColor = {
    draft: "subtle",
    in_progress: "warning",
    completed: "success",
    cancelled: "subtle",
};

const errorText = (error) => {
    const trace = error?.traceId ? `（追踪号 ${error.traceId}）` : "";
    return `${error?.message || "操作失败"}${trace}`;
};

const emptyCompletion = {
    completedDate: null,
    serviceContent: "",
    result: "",
    costAmount: "",
    currency: "",
    usageValueAtService: "",
    usageUnit: "",
    nextDueDate: null,
    resultingEquipmentStatus: "available",
    reason: "",
};

const numberOrNull = (value) => {
    const text = (value ?? "").toString().trim();
    if (text === "") {
        return null;
    }
    const parsed = Number(text);
    return Number.isFinite(parsed) ? parsed : null;
};

const trimmedOrNull = (value) => {
    const text = (value ?? "").toString().trim();
    return text === "" ? null : text;
};

/**
 * Maintenance and repair work orders.
 *
 * One component serves both the cross-equipment page and the per-equipment tab: pass
 * `equipmentId` to pin it to a single unit. Splitting them into two implementations is how the
 * two views end up disagreeing about what a filter means.
 *
 * Every row's available operations come from the server's `allowedActions`, and the completion
 * dialog's target statuses from `allowedResultingStatuses`. Neither is re-derived here — a
 * client-side copy of the rules drifts, and the symptom is a menu whose entries answer 403/409.
 */
export const ServiceRecordPanel = ({ equipmentId = null, equipment = null, canWrite = false, onChanged }) => {
    const styles = useStyles();
    const showToast = useToast();
    const nav = useNavigate();
    const { openDrawer, closeDrawer, drawer } = useDrawer({ style: { width: 720 } });

    const defaultQuery = useMemo(
        () => ({
            serviceType: "",
            status: "",
            includeVoided: false,
            keyword: "",
            completedFrom: null,
            completedTo: null,
            page: 1,
            pageSize: EQUIPMENT_DEFAULT_PAGE_SIZE,
            // A single unit's history is a timeline: ordered by when the work happened, with open
            // orders pinned on top (design 8.4). The cross-equipment list is a ledger instead, so
            // it stays on entry time.
            sortBy: equipmentId ? "timeline" : "createdAtUtc",
            sortDirection: "desc",
        }),
        [equipmentId]
    );

    const [query, setQuery] = useState(defaultQuery);
    const [keywordInput, setKeywordInput] = useState("");
    const [page, setPage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState("");

    const [completeTarget, setCompleteTarget] = useState(null);
    const [completion, setCompletion] = useState(emptyCompletion);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelReason, setCancelReason] = useState("");
    const [voidTarget, setVoidTarget] = useState(null);
    const [voidReason, setVoidReason] = useState("");
    // Raised by a 409 EquipmentInUse: { item, sessions } naming the jobs holding the unit.
    const [occupancyPrompt, setOccupancyPrompt] = useState(null);
    const [busy, setBusy] = useState(false);

    // Monotonic request id: a slow older response must never overwrite a newer one.
    const requestRef = useRef(0);
    // Separate counter for the edit flow: it races on which drawer opens, not on which list wins.
    const formRequestRef = useRef(0);
    const queryRef = useRef(query);

    const loadList = useCallback(
        async (activeQuery) => {
            const seq = ++requestRef.current;
            setLoading(true);
            setListError("");
            try {
                const result = await listServiceRecords(equipmentId, {
                    ...activeQuery,
                    completedFrom: toDateOnly(activeQuery.completedFrom),
                    completedTo: toDateOnly(activeQuery.completedTo),
                });
                if (seq === requestRef.current) {
                    setPage(result);
                }
            } catch (error) {
                console.error("Failed to load service records:", error);
                if (seq === requestRef.current) {
                    // Not an empty list: reporting "cannot read" as "there are none" would tell the
                    // operator this unit has never been serviced.
                    setPage(null);
                    setListError(errorText(error));
                }
            } finally {
                if (seq === requestRef.current) {
                    setLoading(false);
                }
            }
        },
        [equipmentId]
    );

    useEffect(() => {
        queryRef.current = query;
        loadList(query);
    }, [loadList, query]);

    const reload = useCallback(() => {
        loadList(queryRef.current);
        onChanged?.();
    }, [loadList, onChanged]);

    const patchQuery = (patch) => setQuery((previous) => ({ ...previous, ...patch, page: 1 }));

    const runMutation = async (action, successMessage) => {
        setBusy(true);
        try {
            await action();
            showToast(successMessage, "success");
            reload();
            return true;
        } catch (error) {
            console.error("Service record mutation failed:", error);
            showToast("操作失败", "error", errorText(error));
            return false;
        } finally {
            setBusy(false);
        }
    };

    /**
     * Starting a work order on a unit that is out on a job (design 4.2).
     *
     * The plain attempt goes first. If the unit is occupied the server refuses with the sessions
     * that hold it, and only then is the operator asked to confirm ending that occupancy — naming
     * the session is the confirmation, so it has to come from a person who has seen which job it is.
     * Retrying automatically would be the same as not asking.
     */
    const startRecord = async (item, releaseFromSessionId) => {
        setBusy(true);
        try {
            await startServiceRecord(item.equipmentId, item.id, null, releaseFromSessionId);
            showToast(
                releaseFromSessionId
                    ? "已结束巡检占用，器材转入维修保养中"
                    : "维保已开始，器材转入维修保养中",
                "success"
            );
            reload();
        } catch (error) {
            const sessions = occupyingSessionsFromError(error);
            if (sessions.length > 0 && !releaseFromSessionId) {
                setOccupancyPrompt({ item, sessions });
                return;
            }

            console.error("Service record start failed:", error);
            showToast("操作失败", "error", errorText(error));
        } finally {
            setBusy(false);
        }
    };

    /**
     * The form needs the owning equipment. On the per-equipment tab it is already loaded; on the
     * cross-equipment page each row names a different unit, so it is fetched on demand.
     */
    const openForm = async (record, rowEquipmentId) => {
        // Two awaits stand between the click and the drawer, so a slow first click can open its
        // form after a fast second one and quietly replace it. The operator would then be editing
        // the record they did not click last. Only the newest click may open a drawer.
        const seq = ++formRequestRef.current;
        const isCurrent = () => seq === formRequestRef.current;

        let owner = equipment;
        if (!owner || (rowEquipmentId && owner.id !== rowEquipmentId)) {
            try {
                owner = await getEquipment(rowEquipmentId || equipmentId);
            } catch (error) {
                console.error("Failed to load equipment for the service record form:", error);
                if (isCurrent()) {
                    showToast("无法加载器材信息", "error", errorText(error));
                }
                return;
            }
        }

        if (!isCurrent()) {
            return;
        }

        // The row is a list item, which omits the long-form columns — fault description, service
        // content, result, usage. The edit endpoint overwrites every field, so handing the list
        // shape to the form would post those back as null and silently erase them. Load the detail.
        let full = record;
        if (record?.id) {
            try {
                full = await getServiceRecord(owner.id, record.id);
            } catch (error) {
                console.error("Failed to load the service record before editing:", error);
                if (isCurrent()) {
                    showToast("无法加载维保记录", "error", errorText(error));
                }
                return;
            }
        }

        if (!isCurrent()) {
            return;
        }

        openDrawer({
            header: record ? "编辑维保记录" : "新建维保记录",
            body: (
                <ServiceRecordForm
                    equipment={owner}
                    record={full}
                    onSaved={({ close }) => {
                        reload();
                        if (close) {
                            closeDrawer();
                        }
                    }}
                    onCancel={closeDrawer}
                />
            ),
        });
    };

    const openAttachments = (record) => {
        // A pending edit lookup must not replace the attachment drawer after it opens.
        formRequestRef.current += 1;
        openDrawer({
            header: `维保附件 · ${record.recordNo || ""}`,
            body: (
                // Keyed by what it is filtered on. Without this React reuses the same instance when
                // the drawer is reopened for a different record — same component type, same
                // position — and the previous record's page and rows stay on screen until the new
                // request lands. Those stale rows are live: the download and delete routes only
                // check equipmentId, so a file belonging to another work order on the same unit can
                // still be acted on.
                <AttachmentPanel
                    key={`${record.equipmentId}:${record.id}`}
                    equipmentId={record.equipmentId}
                    serviceRecordId={record.id}
                    canWrite={canWrite}
                />
            ),
        });
    };

    const openComplete = (record) => {
        setCompleteTarget(record);
        setCompletion({
            ...emptyCompletion,
            // Pre-selects the first target the server says this caller may use.
            resultingEquipmentStatus: (record.allowedResultingStatuses || [])[0] || "available",
        });
    };

    const submitComplete = async () => {
        const target = completeTarget;
        if (!target) {
            return;
        }
        const done = await runMutation(
            () =>
                completeServiceRecord(target.equipmentId, target.id, {
                    recordVersion: target.version,
                    completedDate: toDateOnly(completion.completedDate),
                    serviceContent: trimmedOrNull(completion.serviceContent),
                    result: trimmedOrNull(completion.result),
                    costAmount: numberOrNull(completion.costAmount),
                    currency: trimmedOrNull(completion.currency),
                    usageValueAtService: numberOrNull(completion.usageValueAtService),
                    usageUnit: trimmedOrNull(completion.usageUnit),
                    nextDueDate: toDateOnly(completion.nextDueDate),
                    resultingEquipmentStatus: completion.resultingEquipmentStatus,
                    reason: trimmedOrNull(completion.reason),
                }),
            "维保记录已完成"
        );
        if (done) {
            setCompleteTarget(null);
        }
    };

    const submitCancel = async () => {
        const target = cancelTarget;
        if (!target) {
            return;
        }
        const done = await runMutation(
            () => cancelServiceRecord(target.equipmentId, target.id, cancelReason.trim()),
            "维保记录已取消"
        );
        if (done) {
            setCancelTarget(null);
        }
    };

    const submitVoid = async () => {
        const target = voidTarget;
        if (!target) {
            return;
        }
        const done = await runMutation(
            () => voidServiceRecord(target.equipmentId, target.id, voidReason.trim()),
            "维保记录已作废"
        );
        if (done) {
            setVoidTarget(null);
        }
    };

    const allows = (record, action) => (record.allowedActions || []).includes(action);

    const columns = useMemo(() => {
        const cells = [
            FC.createTableColumn({
                columnId: "recordNo",
                renderHeaderCell: () => "记录编号",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <span className={styles.mono}>{item.recordNo || "-"}</span>
                        {item.isVoided && (
                            <FC.Badge appearance="tint" color="danger" size="small" style={{ marginLeft: 6 }}>
                                已作废
                            </FC.Badge>
                        )}
                    </FC.TableCellLayout>
                ),
            }),
        ];

        if (!equipmentId) {
            cells.push(
                FC.createTableColumn({
                    columnId: "equipment",
                    renderHeaderCell: () => "器材",
                    renderCell: (item) => (
                        <FC.TableCellLayout>
                            <FC.Link
                                className={styles.mono}
                                onClick={() => nav(`/equipment/${item.equipmentId}`)}
                            >
                                {item.equipmentAssetCode}
                            </FC.Link>
                            <span style={{ marginLeft: 6 }}>{item.equipmentName}</span>
                        </FC.TableCellLayout>
                    ),
                })
            );
        }

        cells.push(
            FC.createTableColumn({
                columnId: "serviceType",
                renderHeaderCell: () => "类型",
                renderCell: (item) => <FC.TableCellLayout>{serviceTypeLabel(item.serviceType)}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "status",
                renderHeaderCell: () => "状态",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <FC.Badge appearance="tint" color={statusBadgeColor[item.status] || "subtle"}>
                            {serviceStatusLabel(item.status)}
                        </FC.Badge>
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "reportedDate",
                renderHeaderCell: () => "报告",
                renderCell: (item) => <FC.TableCellLayout>{formatDateOnly(item.reportedDate) || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "startedDate",
                renderHeaderCell: () => "开始",
                renderCell: (item) => <FC.TableCellLayout>{formatDateOnly(item.startedDate) || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "completedDate",
                renderHeaderCell: () => "完成",
                renderCell: (item) => <FC.TableCellLayout>{formatDateOnly(item.completedDate) || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "nextDueDate",
                renderHeaderCell: () => "下次到期",
                renderCell: (item) => <FC.TableCellLayout>{formatDateOnly(item.nextDueDate) || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "cost",
                renderHeaderCell: () => "费用",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        {item.costAmount === null || item.costAmount === undefined
                            ? "-"
                            : `${item.costAmount} ${item.currency || ""}`.trim()}
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "technician",
                renderHeaderCell: () => "服务商 / 人员",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        {[item.serviceProvider, item.technicianName].filter(Boolean).join(" / ") || "-"}
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "updatedAtUtc",
                renderHeaderCell: () => "更新时间",
                renderCell: (item) => <FC.TableCellLayout>{toDateTimeString(item.updatedAtUtc)}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "ops",
                renderHeaderCell: () => "操作",
                renderCell: (item) => {
                    // Rendered straight from allowedActions. No status checks here on purpose.
                    const options = [
                        // Always available: reading a work order's files needs no write permission,
                        // and the panel itself hides the upload bar when the caller cannot write.
                        <FC.MenuItem key="attachments" onClick={() => openAttachments(item)}>
                            附件
                        </FC.MenuItem>,
                        allows(item, serviceRecordActions.edit) && (
                            <FC.MenuItem key="edit" onClick={() => openForm(item, item.equipmentId)}>
                                编辑
                            </FC.MenuItem>
                        ),
                        allows(item, serviceRecordActions.start) && (
                            <FC.MenuItem key="start" onClick={() => startRecord(item)}>
                                开始处理
                            </FC.MenuItem>
                        ),
                        allows(item, serviceRecordActions.complete) && (
                            <FC.MenuItem key="complete" onClick={() => openComplete(item)}>
                                完成
                            </FC.MenuItem>
                        ),
                        allows(item, serviceRecordActions.cancel) && (
                            <FC.MenuItem
                                key="cancel"
                                onClick={() => {
                                    setCancelTarget(item);
                                    setCancelReason("");
                                }}
                            >
                                取消
                            </FC.MenuItem>
                        ),
                        allows(item, serviceRecordActions.void) && (
                            <FC.MenuItem
                                key="void"
                                onClick={() => {
                                    setVoidTarget(item);
                                    setVoidReason("");
                                }}
                            >
                                作废
                            </FC.MenuItem>
                        ),
                    ].filter(Boolean);

                    if (options.length === 0) {
                        return <FC.TableCellLayout>-</FC.TableCellLayout>;
                    }

                    return (
                        <FC.TableCellLayout>
                            <FC.Menu>
                                <FC.MenuTrigger disableButtonEnhancement>
                                    <FC.Button appearance="subtle" icon={<IC.MoreVerticalRegular />} />
                                </FC.MenuTrigger>
                                <FC.MenuPopover>
                                    <FC.MenuList>{options}</FC.MenuList>
                                </FC.MenuPopover>
                            </FC.Menu>
                        </FC.TableCellLayout>
                    );
                },
            })
        );

        return cells;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [equipmentId, nav, styles.mono]);

    const items = page?.items || [];
    const totalPages = page?.totalPages || 0;
    const currentPage = page?.page || query.page;

    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <span className={styles.muted}>
                    {equipmentId ? "该器材的维修保养记录" : "全部器材的维修保养记录"}
                </span>
                {canWrite && equipmentId && (
                    <FC.Button appearance="primary" icon={<IC.AddRegular />} onClick={() => openForm(null, equipmentId)}>
                        新建维保记录
                    </FC.Button>
                )}
            </div>

            <div className={styles.filters}>
                <div className={styles.field} style={{ minWidth: 240 }}>
                    <FC.Label>关键字</FC.Label>
                    <FC.Input
                        contentBefore={<IC.SearchFilled />}
                        placeholder="记录编号、服务商、人员、器材编号或名称"
                        value={keywordInput}
                        onChange={(_, data) => setKeywordInput(data.value)}
                        onKeyDown={(evt) => {
                            if (evt.key === "Enter") {
                                patchQuery({ keyword: keywordInput.trim() });
                            }
                        }}
                    />
                </div>
                <div className={styles.field}>
                    <FC.Label>类型</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.serviceType ? [query.serviceType] : [""]}
                        value={serviceTypeLabel(query.serviceType) || "全部类型"}
                        onOptionSelect={(_, data) => patchQuery({ serviceType: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部类型</FC.Option>
                        {serviceTypeOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>状态</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.status ? [query.status] : [""]}
                        value={serviceStatusLabel(query.status) || "全部状态"}
                        onOptionSelect={(_, data) => patchQuery({ status: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部状态</FC.Option>
                        {serviceStatusOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>完成日期起</FC.Label>
                    <FDP.DatePicker
                        value={query.completedFrom}
                        placeholder="不限"
                        formatDate={toDateString}
                        onSelectDate={(date) => patchQuery({ completedFrom: date || null })}
                    />
                </div>
                <div className={styles.field}>
                    <FC.Label>完成日期止</FC.Label>
                    <FDP.DatePicker
                        value={query.completedTo}
                        placeholder="不限"
                        formatDate={toDateString}
                        onSelectDate={(date) => patchQuery({ completedTo: date || null })}
                    />
                </div>
                <div className={styles.field}>
                    <FC.Label>排序</FC.Label>
                    <FC.Dropdown
                        selectedOptions={[query.sortBy]}
                        value={serviceRecordSortOptions.find((o) => o.value === query.sortBy)?.label || ""}
                        onOptionSelect={(_, data) => patchQuery({ sortBy: data.optionValue })}
                    >
                        {serviceRecordSortOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <FC.Checkbox
                    checked={query.includeVoided}
                    label="包含已作废"
                    onChange={(_, data) => patchQuery({ includeVoided: Boolean(data.checked) })}
                />
                <FC.Button
                    appearance="primary"
                    disabled={loading}
                    onClick={() => patchQuery({ keyword: keywordInput.trim() })}
                >
                    {loading ? "查询中..." : "查询"}
                </FC.Button>
                <FC.Button
                    onClick={() => {
                        setKeywordInput("");
                        setQuery(defaultQuery);
                    }}
                >
                    重置
                </FC.Button>
            </div>

            {listError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{listError}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <SimpleGrid items={items} columns={columns} />
            {!loading && items.length === 0 && !listError && (
                <div className={styles.muted}>没有符合条件的维保记录</div>
            )}

            <div className={styles.actions}>
                <span className={styles.muted}>
                    {`共 ${page?.totalCount || 0} 条，第 ${currentPage} / ${Math.max(totalPages, 1)} 页`}
                </span>
                <div className={styles.actions}>
                    <FC.Label>每页</FC.Label>
                    <FC.Dropdown
                        style={{ minWidth: 90 }}
                        selectedOptions={[String(query.pageSize)]}
                        value={String(query.pageSize)}
                        onOptionSelect={(_, data) =>
                            setQuery((previous) => ({ ...previous, pageSize: Number(data.optionValue), page: 1 }))
                        }
                    >
                        {pageSizeOptions.map((size) => (
                            <FC.Option key={size} value={String(size)}>
                                {String(size)}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                    <FC.Button
                        disabled={loading || currentPage <= 1}
                        onClick={() => setQuery((previous) => ({ ...previous, page: currentPage - 1 }))}
                    >
                        上一页
                    </FC.Button>
                    <FC.Button
                        disabled={loading || currentPage >= totalPages}
                        onClick={() => setQuery((previous) => ({ ...previous, page: currentPage + 1 }))}
                    >
                        下一页
                    </FC.Button>
                </div>
            </div>

            <FC.Dialog
                open={Boolean(completeTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setCompleteTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>完成维保</FC.DialogTitle>
                        <FC.DialogContent>
                            <div className={styles.dialogBody}>
                                <div className={styles.muted}>
                                    {`${completeTarget?.recordNo || ""} · ${serviceTypeLabel(completeTarget?.serviceType)}`}
                                </div>
                                <div className={styles.row}>
                                    <FC.Field label="完成日期" hint="留空按服务器业务日期（Asia/Shanghai）">
                                        <FDP.DatePicker
                                            value={completion.completedDate}
                                            placeholder="按业务日期"
                                            formatDate={toDateString}
                                            onSelectDate={(date) =>
                                                setCompletion((p) => ({ ...p, completedDate: date || null }))
                                            }
                                        />
                                    </FC.Field>
                                    <FC.Field label="下次到期日" hint="必须晚于完成日期">
                                        <FDP.DatePicker
                                            value={completion.nextDueDate}
                                            placeholder="不设置"
                                            formatDate={toDateString}
                                            onSelectDate={(date) =>
                                                setCompletion((p) => ({ ...p, nextDueDate: date || null }))
                                            }
                                        />
                                    </FC.Field>
                                </div>
                                <FC.Field label="处理内容">
                                    <FC.Textarea
                                        rows={2}
                                        value={completion.serviceContent}
                                        onChange={(_, data) =>
                                            setCompletion((p) => ({ ...p, serviceContent: data.value }))
                                        }
                                    />
                                </FC.Field>
                                <FC.Field label="结果">
                                    <FC.Textarea
                                        rows={2}
                                        value={completion.result}
                                        onChange={(_, data) => setCompletion((p) => ({ ...p, result: data.value }))}
                                    />
                                </FC.Field>
                                <div className={styles.row}>
                                    <FC.Field label="费用">
                                        <FC.Input
                                            type="number"
                                            min={0}
                                            value={completion.costAmount}
                                            onChange={(_, data) =>
                                                setCompletion((p) => ({ ...p, costAmount: data.value }))
                                            }
                                        />
                                    </FC.Field>
                                    <FC.Field label="币种" hint="ISO 4217 三字码，留空沿用原值">
                                        <FC.Input
                                            maxLength={3}
                                            value={completion.currency}
                                            onChange={(_, data) =>
                                                setCompletion((p) => ({ ...p, currency: data.value.toUpperCase() }))
                                            }
                                        />
                                    </FC.Field>
                                </div>
                                <div className={styles.row}>
                                    <FC.Field label="使用量">
                                        <FC.Input
                                            type="number"
                                            value={completion.usageValueAtService}
                                            onChange={(_, data) =>
                                                setCompletion((p) => ({ ...p, usageValueAtService: data.value }))
                                            }
                                        />
                                    </FC.Field>
                                    <FC.Field label="使用量单位">
                                        <FC.Dropdown
                                            selectedOptions={completion.usageUnit ? [completion.usageUnit] : [""]}
                                            value={
                                                serviceUsageUnitOptions.find((o) => o.value === completion.usageUnit)
                                                    ?.label || "沿用原值"
                                            }
                                            onOptionSelect={(_, data) =>
                                                setCompletion((p) => ({ ...p, usageUnit: data.optionValue || "" }))
                                            }
                                        >
                                            <FC.Option value="">沿用原值</FC.Option>
                                            {serviceUsageUnitOptions.map((option) => (
                                                <FC.Option key={option.value} value={option.value}>
                                                    {option.label}
                                                </FC.Option>
                                            ))}
                                        </FC.Dropdown>
                                    </FC.Field>
                                </div>
                                <FC.Field label="完成后器材状态" required>
                                    {/* Straight from allowedResultingStatuses — the server already
                                        filtered it by the state machine and by this caller's role. */}
                                    <FC.Dropdown
                                        selectedOptions={[completion.resultingEquipmentStatus]}
                                        value={equipmentOperationalStatusLabel(completion.resultingEquipmentStatus)}
                                        onOptionSelect={(_, data) =>
                                            setCompletion((p) => ({ ...p, resultingEquipmentStatus: data.optionValue }))
                                        }
                                    >
                                        {(completeTarget?.allowedResultingStatuses || []).map((value) => (
                                            <FC.Option key={value} value={value}>
                                                {equipmentOperationalStatusLabel(value)}
                                            </FC.Option>
                                        ))}
                                    </FC.Dropdown>
                                </FC.Field>
                                <FC.Field label="原因" hint="退役与报废必须填写原因">
                                    <FC.Textarea
                                        rows={2}
                                        value={completion.reason}
                                        onChange={(_, data) => setCompletion((p) => ({ ...p, reason: data.value }))}
                                    />
                                </FC.Field>
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setCompleteTarget(null)}>
                                取消
                            </FC.Button>
                            <FC.Button appearance="primary" disabled={busy} onClick={submitComplete}>
                                提交
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(cancelTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setCancelTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>取消维保记录</FC.DialogTitle>
                        <FC.DialogContent>
                            <div className={styles.dialogBody}>
                                <div className={styles.muted}>
                                    {`${cancelTarget?.recordNo || ""}。若已开始处理，器材会恢复到送修前的状态。`}
                                </div>
                                <FC.Field label="取消原因" required>
                                    <FC.Textarea
                                        rows={3}
                                        value={cancelReason}
                                        onChange={(_, data) => setCancelReason(data.value)}
                                    />
                                </FC.Field>
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setCancelTarget(null)}>
                                返回
                            </FC.Button>
                            <FC.Button
                                appearance="primary"
                                disabled={busy || !cancelReason.trim()}
                                onClick={submitCancel}
                            >
                                提交
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(voidTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setVoidTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>作废维保记录</FC.DialogTitle>
                        <FC.DialogContent>
                            <div className={styles.dialogBody}>
                                <div className={styles.muted}>
                                    {`${voidTarget?.recordNo || ""}。作废后该记录不再参与保养日期计算；如果器材当前状态正是这条记录造成的，会一并回退。`}
                                </div>
                                <FC.Field label="作废原因" required>
                                    <FC.Textarea
                                        rows={3}
                                        value={voidReason}
                                        onChange={(_, data) => setVoidReason(data.value)}
                                    />
                                </FC.Field>
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setVoidTarget(null)}>
                                返回
                            </FC.Button>
                            <FC.Button appearance="primary" disabled={busy || !voidReason.trim()} onClick={submitVoid}>
                                提交
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(occupancyPrompt)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setOccupancyPrompt(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>结束巡检占用并送修</FC.DialogTitle>
                        <FC.DialogContent>
                            <div className={styles.dialogBody}>
                                <div className={styles.muted}>
                                    该器材正被以下巡检会话占用。确认后将在同一事务中结束占用区间，再把器材转入维修保养中。
                                </div>
                                {(occupancyPrompt?.sessions || []).map((session) => (
                                    <FC.Button
                                        key={session.sessionId}
                                        appearance="primary"
                                        disabled={busy}
                                        onClick={() => {
                                            const target = occupancyPrompt.item;
                                            setOccupancyPrompt(null);
                                            startRecord(target, session.sessionId);
                                        }}
                                    >
                                        {`结束会话 ${session.sessionSeq} 的占用并开始维保`}
                                    </FC.Button>
                                ))}
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setOccupancyPrompt(null)}>
                                取消
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            {drawer}
        </div>
    );
};
